import {
  NUTRITION_NEW_APPLICATION_LABEL,
  NUTRITION_TASK_NO_DATE_LABEL,
  NUTRITION_TASK_NO_PRODUCTS_LABEL,
} from 'constants/uiConstants';
import React from 'react';
import { BiCheck } from 'react-icons/bi';
import { LuPlus } from 'react-icons/lu';
import MergeClassNames from 'utils/MergeClassNames';
import {
  NEW_APPLICATION_TARGET,
  type NutritionTaskTarget,
} from './nutritionIntents';

interface TaskTargetCardsProps {
  /** Labels the group, e.g. "Add to". */
  label: string;
  targets: NutritionTaskTarget[];
  value: string | null;
  onChange: (value: string) => void;
  /** Outlines the cards in red when nothing has been chosen yet. */
  invalid?: boolean;
}

const CARD =
  'flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-left has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-success';

/**
 * Where added products go (OP-2523): the plan's planned applications and
 * sowings as cards in date order — when (date and timing) in bold with the
 * type beside it, then every product already on it with its rate — and a
 * new application as a dashed card at the end. All visible at once, as a
 * plan has only a handful. A radio group underneath, so arrow keys move
 * between cards. The chosen card is marked as the Tasks picker lists mark a
 * selection (SeedTargetList): green fill, bold heading and a tick.
 */
const TaskTargetCards: React.FC<TaskTargetCardsProps> = ({
  label,
  targets,
  value,
  onChange,
  invalid = false,
}) => (
  <div
    role="radiogroup"
    aria-label={label}
    className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1"
  >
    {targets.map((target) => {
      const selected = target.value === value;
      const isNew = target.value === NEW_APPLICATION_TARGET;
      const details = target.details;
      return (
        <label
          key={target.value}
          className={MergeClassNames(
            CARD,
            isNew ? 'border-dashed' : 'border-solid',
            selected
              ? 'border-gray-medium bg-success-light'
              : invalid
                ? 'border-danger bg-white hover:bg-rowHover'
                : 'border-gray-medium bg-white hover:bg-rowHover'
          )}
        >
          <input
            type="radio"
            className="sr-only"
            name="nutrition-add-target"
            value={target.value}
            checked={selected}
            aria-label={target.label}
            onChange={() => onChange(target.value)}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            {isNew || !details ? (
              <span className="inline-flex items-center gap-1.5 font-semibold text-charcoal">
                <LuPlus size={14} aria-hidden />
                {NUTRITION_NEW_APPLICATION_LABEL}
              </span>
            ) : (
              <>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-semibold text-charcoal">
                    {[
                      details.date ?? NUTRITION_TASK_NO_DATE_LABEL,
                      details.timing,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <span className="rounded bg-secondary-light px-1.5 text-sm text-gray-dark">
                    {details.type}
                  </span>
                </span>
                <span className="text-sm text-gray-dark">
                  {details.products.length > 0
                    ? details.products.join(' · ')
                    : NUTRITION_TASK_NO_PRODUCTS_LABEL}
                </span>
              </>
            )}
          </span>
          {selected && (
            <BiCheck
              className="flex-shrink-0 text-lg text-success-dark"
              aria-hidden="true"
            />
          )}
        </label>
      );
    })}
  </div>
);

export default TaskTargetCards;
